import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ArticleCard from '@/components/feature/ArticleCard';
import HotTopics from '@/components/feature/HotTopics';
import NewsletterForm from '@/components/feature/NewsletterForm';
import { articles, categories, getFeaturedArticles, getHotArticles, getLatestArticles } from '@/mocks/articles';

export default function Home() {
  const featuredArticles = getFeaturedArticles();
  const hotArticles = getHotArticles();
  const latestArticles = getLatestArticles(8);

  const heroArticle = featuredArticles[0];
  const subFeatured = featuredArticles.slice(1, 3);

  const canonicalUrl = window.location.origin;

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '51吃瓜网',
    url: canonicalUrl,
    description: '51吃瓜网是专注于娱乐热点、明星八卦、社会爆料的综合吃瓜平台。第一时间获取最新吃瓜资讯与独家爆料内容。',
    logo: `${canonicalUrl}/logo.png`,
    sameAs: [
      'https://weibo.com/51chiagua',
      'https://www.zhihu.com/org/51chiagua',
    ],
  };

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '51吃瓜网',
    url: canonicalUrl,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${canonicalUrl}/search?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };

  return (
    <>
      <title>51吃瓜网 - 最新热点吃瓜爆料平台 | 娱乐热点 明星八卦 社会爆料</title>
      <meta name="description" content="51吃瓜网是专注于娱乐热点、明星八卦、社会爆料的综合吃瓜平台。在这里你可以第一时间获取最新吃瓜资讯、热门话题讨论与独家爆料内容，和千万吃瓜群众一起围观热点事件，不错过任何精彩瓜。" />
      <meta name="keywords" content="51吃瓜网,吃瓜爆料,明星八卦,娱乐热点,社会爆料,最新吃瓜,热门话题讨论,独家瓜料" />
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:title" content="51吃瓜网 - 最新热点吃瓜爆料平台" />
      <meta property="og:description" content="娱乐热点、明星八卦、社会爆料，最新吃瓜资讯一网打尽！和千万吃瓜群众一起围观热点事件。" />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content="51吃瓜网" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="51吃瓜网 - 最新热点吃瓜爆料平台" />
      <meta name="twitter:description" content="娱乐热点、明星八卦、社会爆料，最新吃瓜资讯一网打尽！" />

      <ScrollToTop />
      <Navbar />

      <main>
        <section className="relative h-[500px] md:h-[640px] overflow-hidden" aria-label="头条推荐">
          {heroArticle && (
            <Link
              to={`/article/${heroArticle.id}`}
              className="block absolute inset-0 cursor-pointer"
            >
              <img
                src={heroArticle.imageUrl}
                alt={heroArticle.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/50"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12 max-w-4xl">
                <span className="inline-block px-3 py-1 text-xs font-medium bg-primary-500 text-white rounded-full mb-4 whitespace-nowrap">
                  {heroArticle.category}
                </span>
                <h1 className="text-2xl md:text-4xl font-heading text-white mb-3 leading-tight">
                  {heroArticle.title}
                </h1>
                <p className="text-white/75 text-sm md:text-base max-w-2xl line-clamp-2 mb-4">
                  {heroArticle.excerpt}
                </p>
                <div className="flex items-center gap-4 text-white/50 text-xs">
                  <span>{heroArticle.publishedAt}</span>
                  <span>{heroArticle.viewCount.toLocaleString()} 阅读</span>
                  <span>{heroArticle.commentCount.toLocaleString()} 评论</span>
                </div>
              </div>
            </Link>
          )}
        </section>

        <section className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14" aria-label="子头条">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {subFeatured.map((article) => (
              <Link
                key={article.id}
                to={`/article/${article.id}`}
                className="group relative h-56 md:h-64 rounded-lg overflow-hidden cursor-pointer"
              >
                <img
                  src={article.imageUrl}
                  alt={article.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <span className="inline-block px-2.5 py-0.5 text-xs font-medium bg-accent-500 text-white rounded-full mb-2 whitespace-nowrap">
                    {article.category}
                  </span>
                  <h2 className="text-lg font-heading text-white leading-snug group-hover:text-accent-300 transition-colors">
                    {article.title}
                  </h2>
                </div>
              </Link>
            ))}
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 md:px-6 pb-10 md:pb-14" aria-label="分类导航">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-foreground-600 mr-2 whitespace-nowrap">热门分类：</span>
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="px-4 py-2 text-sm font-medium rounded-full bg-background-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className={`${cat.icon} w-4 h-4 mr-1.5 inline-flex items-center justify-center align-text-bottom`}></i>
                {cat.name}
              </Link>
            ))}
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 md:px-6 pb-14" aria-label="最新文章">
          <div className="flex flex-col lg:flex-row gap-8">
            <div className="flex-1">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl md:text-2xl font-heading text-foreground-950">
                  最新吃瓜
                </h2>
                <Link
                  to="/category/entertainment"
                  className="text-sm text-primary-500 hover:text-primary-600 transition-colors flex items-center gap-1 whitespace-nowrap"
                >
                  查看全部
                  <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                </Link>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {latestArticles.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            </div>

            <div className="lg:w-80 shrink-0">
              <div className="sticky top-24 space-y-6">
                <HotTopics articles={hotArticles} />
              </div>
            </div>
          </div>
        </section>

        <section className="bg-background-100" aria-label="Newsletter 订阅">
          <div className="max-w-3xl mx-auto px-4 md:px-6 py-14 text-center">
            <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center bg-accent-100 rounded-full">
              <i className="ri-mail-send-line text-accent-500 w-6 h-6 flex items-center justify-center"></i>
            </div>
            <h2 className="text-xl md:text-2xl font-heading text-foreground-950 mb-3">
              不错过任何精彩瓜
            </h2>
            <p className="text-foreground-600 text-sm mb-6 max-w-md mx-auto">
              订阅51吃瓜网，最新热点爆料第一时间直达您的邮箱。和千万吃瓜群众一起围观！
            </p>
            <NewsletterForm />
          </div>
        </section>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(websiteSchema) }}
      />
    </>
  );
}